import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { toBase64 } from '../services/toBase64';
import ApiService from '../services/ApiService';
import Nav from '../components/Nav';
import Loading from '../components/Loading';
import MediaCarousel from '../components/modals/MediaCarousel';

const AddMemory = () => {
    const { id } = useParams();

    const navigate = useNavigate();

    const videoInputRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [cloneData, setCloneData] = useState([])
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [date, setDate] = useState("");
    const [isCarouselOpen, setIsCarouselOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [mediaType, setMediaType] = useState('');


    const openCarousel = (index, type) => {
        setCurrentMediaIndex(index);
        setMediaType(type);
        setIsCarouselOpen(true);
    };

    const closeCarousel = () => {
        setIsCarouselOpen(false);
    };

    const nextMedia = () => {
        if (mediaType === 'image') {
            setCurrentMediaIndex((prevIndex) => (prevIndex + 1) % memoryData.gallery.length);
        } else if (mediaType === 'video' || mediaType === 'pdf') {
            setCurrentMediaIndex((prevIndex) => (prevIndex + 1) % 1);
        }
    };

    const prevMedia = () => {
        if (mediaType === 'image') {
            setCurrentMediaIndex((prevIndex) => (prevIndex - 1 + memoryData.gallery.length) % memoryData.gallery.length);
        } else if (mediaType === 'video' || mediaType === 'pdf') {
            setCurrentMediaIndex((prevIndex) => (prevIndex - 1 + 1) % 1);
        }
    };


    const [memoryData, setMemoryData] = useState({
        gallery: [],
        video: {},
        pdf: {},
    });

    const getClone = async () => {
        try {
            const response = await ApiService.get(`/cms/clones?id=${id}`);
            if (response?.contents?.length > 0) {
                setCloneData(response.contents[0]);
            } else {
                console.warn("No clone data found.");
                setCloneData(null);
            }
        } catch (err) {
            console.error("Error during fetching clone data:", err);
        }
    };

    const handleImageUpload = async (files) => {
        if (!files.length) return;

        const uploadedGallery = await Promise.all(
            Array.from(files).map(async (file) => ({
                name: file.name,
                media: await toBase64(file),
            }))
        );

        setMemoryData((prevState) => ({
            ...prevState,
            gallery: [...prevState.gallery, ...uploadedGallery],
        }));
    };

    const handleVideoUpload = async (files) => {
        if (!files.length) return;

        // Clear existing video data if present
        if (memoryData?.video?.name) {
            setMemoryData((prevState) => ({ ...prevState, video: {} }));
            if (videoInputRef.current) videoInputRef.current.value = "";
        }

        // Upload the new video
        const file = files[0];
        const uploadedVideo = {
            name: file.name,
            media: await toBase64(file),
        };

        setMemoryData((prevState) => ({ ...prevState, video: uploadedVideo }));
    };

    const handlePdfUpload = async (files) => {
        if (!files.length) return;

        const file = files[0];
        const uploadedPdf = {
            name: file.name,
            media: await toBase64(file),
        };

        setMemoryData((prevState) => ({
            ...prevState,
            pdf: uploadedPdf,
        }));
    };

    const handleDeleteMedia = (type, index = null) => {
        setMemoryData((prev) => {
            const updatedData = { ...prev };
            if (type === "gallery" && index !== null) {
                updatedData.gallery = prev.gallery.filter((_, i) => i !== index);
            } else if (type === "video") {
                updatedData.video = {};
            } else if (type === "pdf") {
                updatedData.pdf = {};
            }

            if (type === "gallery" && document.getElementById("add-image")) {
                document.getElementById("add-image").value = "";
            }
            if (type === "video" && document.getElementById("add-video")) {
                document.getElementById("add-video").value = "";
            }
            if (type === "pdf" && document.getElementById("add-file")) {
                document.getElementById("add-file").value = "";
            }

            return updatedData;
        });
    };

    const handleSave = async () => {
        try {
            setDisabled(true);
            setLoading(true);

            const dataToSubmit = {
                ...memoryData,
                name: title,
                description,
                date,
                clone: id,
                clone_id: "#" + id
            };

            await ApiService.post(`/cms/memories`, { data: dataToSubmit });

            toast.success('Memory created successfully!', {
                position: 'top-center',
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            setTimeout(() => {
                navigate("/clone-list");
            }, 2200);
        } catch (error) {
            console.error('Error creating memory:', error);
            setDisabled(false);
            setLoading(false);
            toast.error('Failed to create the memory. Please try again.', {
                position: 'top-center',
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (id) {
            getClone();
        }
    }, [id])


    return (
        <div className="tyn-root memories">
            <Loading status={loading} blur={true}>
                <Nav />
                <div className="tyn-content tyn-add-clone">
                    <div className="tyn-main tyn-content-inner" id="tynMain">
                        <div className="container">
                            <div className="tyn-profile d-flex align-items-center justify-content-between px-5 py-4" style={{ background: 'white', borderRadius: '1rem' }}>
                                <div>
                                    <h3>Add a Memory of {cloneData?.data?.name || "..."}</h3>
                                </div>
                                <div className="buttons d-flex">
                                    <button className="btn btn-primary me-2"
                                        onClick={handleSave}
                                        aria-label="Add a memory">Add
                                    </button>
                                    <Link className="btn btn-light" to="/clone-list" aria-label="Cancel and back to clone list">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                        </svg>
                                        Clone List
                                    </Link>
                                </div>
                            </div>

                            <div className="tyn-profile mt-4">
                                <div className="tyn-profile-details">
                                    <div className="tab-content">
                                        <div className="tab-pane show active" id="profile-edit" tabIndex="0">
                                            <div className="row gy-5">

                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Memory Details</h6>
                                                            <div className="tyn-subtext">Add the memory information</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">
                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="name">
                                                                            Title <span className={title.length < 1 ? "text-danger" : ""}>*</span>
                                                                        </label>                                                                        <div className="form-control-wrap">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                id="memory-title"
                                                                                placeholder="Your Name"
                                                                                onChange={(e) => setTitle(e.target.value)}
                                                                                value={title}
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="birthday">Date</label>
                                                                        <div className="form-control-wrap" id='birthday'>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                id="memory-date"
                                                                                value={date}
                                                                                onChange={(e) => setDate(e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="description">Description</label>
                                                                        <textarea className="form-control"
                                                                            id="description"
                                                                            placeholder="Description..."
                                                                            rows="4"
                                                                            value={description}
                                                                            onChange={(e) => setDescription(e.target.value)}
                                                                        >
                                                                        </textarea>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="row gy-4">

                                                        <div className="col-lg-3">
                                                            <h6>Memory Gallery</h6>
                                                            <div className="tyn-subtext">Add the memory media</div>
                                                        </div>

                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">
                                                                {/* Image Gallery */}
                                                                <div className="col-lg-6">
                                                                    <h6 className="tyn-overline">Image Gallery</h6>
                                                                    <div className="card">
                                                                        <div className="card-body">
                                                                            <div className="row g-3">
                                                                                <div className="col-4" style={{ minHeight: 100 }}>
                                                                                    <label htmlFor="add-image" className="add-image-label upload-square">
                                                                                        <input
                                                                                            type="file"
                                                                                            accept="image/*"
                                                                                            id="add-image"
                                                                                            className="add-image-input"
                                                                                            multiple
                                                                                            onChange={(e) => handleImageUpload(e.target.files)}
                                                                                        />
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-plus-circle-fill" viewBox="0 0 16 16">
                                                                                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8.5 4.5a.5.5 0 0 0-1 0v3h-3a.5.5 0 0 0 0 1h3v3a.5.5 0 0 0 1 0v-3h3a.5.5 0 0 0 0-1h-3z" />
                                                                                        </svg>
                                                                                    </label>
                                                                                </div>

                                                                                {memoryData.gallery.length > 0 && memoryData.gallery.map((picture, index) => (
                                                                                    <div className="col-4" key={index} style={{ position: 'relative' }}>
                                                                                        <Link className="glightbox tyn-thumb" data-gallery="media-photo">
                                                                                            <img src={picture?.media} className="tyn-image pointer" onClick={() => openCarousel(index, 'image')} alt={picture?.name} />
                                                                                        </Link>
                                                                                        <button
                                                                                            className="btn btn-danger delete-icon"
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                top: '10px',
                                                                                                right: '10px',
                                                                                                padding: '5px',
                                                                                                borderRadius: '50%',
                                                                                            }}
                                                                                            onClick={() => handleDeleteMedia('gallery', index)}
                                                                                            aria-label="Delete image"
                                                                                        >
                                                                                            <FaTrash />
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Video */}
                                                                <div className="col-lg-6">
                                                                    <h6 className="tyn-overline">Video Gallery</h6>
                                                                    <div className="card">
                                                                        <div className="card-body">
                                                                            <div className="row g-3">
                                                                                <div className="col-6" style={{ minHeight: 100 }}>
                                                                                    <label htmlFor="add-video" className="add-video-label upload-square">
                                                                                        <input
                                                                                            type="file"
                                                                                            accept="video/*"
                                                                                            id="add-video"
                                                                                            className="add-video-input"
                                                                                            onChange={(e) => handleVideoUpload(e.target.files)}
                                                                                        />
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-plus-circle-fill" viewBox="0 0 16 16">
                                                                                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8.5 4.5a.5.5 0 0 0-1 0v3h-3a.5.5 0 0 0 0 1h3v3a.5.5 0 0 0 1 0v-3h3a.5.5 0 0 0 0-1h-3z" />
                                                                                        </svg>
                                                                                    </label>
                                                                                </div>
                                                                                {memoryData.video?.media && (
                                                                                    <div className="col-6" style={{ position: 'relative' }}>
                                                                                        <video controls width="100%" height="auto" onClick={() => openCarousel(0, 'video')} className='pointer'>
                                                                                            <source src={memoryData.video?.media} type="video/mp4" />
                                                                                        </video>
                                                                                        <button
                                                                                            className="btn btn-danger delete-icon"
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                top: '10px',
                                                                                                right: '10px',
                                                                                                padding: '5px',
                                                                                                borderRadius: '50%',
                                                                                            }}
                                                                                            onClick={() => handleDeleteMedia('video')}
                                                                                            aria-label="Delete video"
                                                                                        >
                                                                                            <FaTrash />
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* PDF Document */}
                                                                <div className="col-lg-12">
                                                                    <h6 className="tyn-overline">File Attachment</h6>
                                                                    <div className="card">
                                                                        <div className="card-body">
                                                                            <div className="row g-3">
                                                                                <div className="col-6" style={{ minHeight: 50 }}>
                                                                                    <label htmlFor="add-file" className="add-file-label upload-square">
                                                                                        <input
                                                                                            type="file"
                                                                                            accept=".pdf"
                                                                                            id="add-file"
                                                                                            className="add-file-input"
                                                                                            onChange={(e) => handlePdfUpload(e.target.files)}
                                                                                        />
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-plus-circle-fill" viewBox="0 0 16 16">
                                                                                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8.5 4.5a.5.5 0 0 0-1 0v3h-3a.5.5 0 0 0 0 1h3v3a.5.5 0 0 0 1 0v-3h3a.5.5 0 0 0 0-1h-3z" />
                                                                                        </svg>
                                                                                    </label>
                                                                                </div>
                                                                                {memoryData.pdf?.name && (
                                                                                    <div className="col-6" style={{ position: 'relative' }}>
                                                                                        <Link to={memoryData.pdf?.media} onClick={() => openCarousel(0, 'pdf')} className="tyn-file">
                                                                                            <div className="tyn-media-group">
                                                                                                <div className="tyn-media tyn-size-lg text-bg-light">
                                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#2C629B" className="bi bi-filetype-pdf" viewBox="0 0 16 16">
                                                                                                        <path fillRule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM1.6 11.85H0v3.999h.791v-1.342h.803q.43 0 .732-.173.305-.175.463-.474a1.4 1.4 0 0 0 .161-.677q0-.375-.158-.677a1.2 1.2 0 0 0-.46-.477q-.3-.18-.732-.179m.545 1.333a.8.8 0 0 1-.085.38.57.57 0 0 1-.238.241.8.8 0 0 1-.375.082H.788V12.48h.66q.327 0 .512.181.185.183.185.522m1.217-1.333v3.999h1.46q.602 0 .998-.237a1.45 1.45 0 0 0 .595-.689q.196-.45.196-1.084 0-.63-.196-1.075a1.43 1.43 0 0 0-.589-.68q-.396-.234-1.005-.234zm.791.645h.563q.371 0 .609.152a.9.9 0 0 1 .354.454q.118.302.118.753a2.3 2.3 0 0 1-.068.592 1.1 1.1 0 0 1-.196.422.8.8 0 0 1-.334.252 1.3 1.3 0 0 1-.483.082h-.563zm3.743 1.763v1.591h-.79V11.85h2.548v.653H7.896v1.117h1.606v.638z" />
                                                                                                    </svg>
                                                                                                </div>
                                                                                                <div className="tyn-media-col">
                                                                                                    <h6 className="name">{memoryData.pdf?.name}</h6>
                                                                                                    <div className="meta">PDF Document</div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </Link>
                                                                                        <button
                                                                                            className="btn btn-danger delete-icon"
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                top: '10px',
                                                                                                right: '10px',
                                                                                                padding: '5px',
                                                                                                borderRadius: '50%',
                                                                                            }}
                                                                                            onClick={() => handleDeleteMedia('pdf')}
                                                                                            aria-label="Delete PDF"
                                                                                        >
                                                                                            <FaTrash />
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="row">
                                                        <div className="d-flex justify-content-end buttons mt-4">
                                                            <button
                                                                className="btn btn-primary me-2"
                                                                disabled={disabled || title.length < 1}
                                                                onClick={handleSave}
                                                                aria-label="Add Memory"
                                                            >
                                                                Add Memory
                                                            </button>
                                                            <button
                                                                className="btn btn-light"
                                                                aria-label="Cancel and back to clone list"
                                                                onClick={() => navigate("/clone-list")}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <MediaCarousel
                    isOpen={isCarouselOpen}
                    closeCarousel={closeCarousel}
                    mediaType={mediaType}
                    memoryData={memoryData}
                    currentMediaIndex={currentMediaIndex}
                    prevMedia={prevMedia}
                    nextMedia={nextMedia}
                />

            </Loading>
        </div >
    )
}

export default AddMemory